import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import KakaoMap from './KakaoMap';
import { parseBuildingGeometry, trimCoordinateInput } from '../utils/coordinates';

interface BuildingInfoEditModalProps {
  building: {
    id: number;
    dongNumber: number;
    dongName?: string | null;
    shapeType?: string | null;
    coordinates?: string | null;
    complex: {
      id: number;
      name: string;
    };
  };
  isOpen: boolean;
  onClose: () => void;
}

interface UpdatePayload {
  dongNumber?: number;
  dongName?: string | null;
  shapeType?: 'polygon' | 'rectangle';
  coordinates?: string | null;
}

const updateBuildingInfo = async (buildingId: number, data: UpdatePayload) => {
  const response = await fetch(`/api/buildings/${buildingId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || '동 정보를 수정하는 데 실패했습니다.');
  }

  return response.json();
};

const BuildingInfoEditModal: React.FC<BuildingInfoEditModalProps> = ({
  building,
  isOpen,
  onClose
}) => {
  const [dongNumberInput, setDongNumberInput] = useState<string>('');
  const [dongNameInput, setDongNameInput] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [initialDongName, setInitialDongName] = useState<string>('');
  const [shapeTypeInput, setShapeTypeInput] = useState<'polygon' | 'rectangle'>('polygon');
  const [initialShapeType, setInitialShapeType] = useState<'polygon' | 'rectangle'>('polygon');
  const [coordinatesInput, setCoordinatesInput] = useState<string>('');
  const [initialCoordinates, setInitialCoordinates] = useState<string>('');
  const [coordinatesError, setCoordinatesError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const formatCoordinatesForInput = (value: unknown): string => {
    if (!value) {
      return '';
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed === '') {
        return '';
      }
      try {
        return JSON.stringify(JSON.parse(trimmed), null, 2);
      } catch (error) {
        return value;
      }
    }
    try {
      return JSON.stringify(value, null, 2);
    } catch (error) {
      return '';
    }
  };

  useEffect(() => {
    if (isOpen && building) {
      setDongNumberInput(building.dongNumber.toString());
      setDongNameInput(building.dongName ?? '');
      setErrorMessage(null);
      setInitialDongName(building.dongName?.trim() ?? '');
      const normalizedShape =
        building.shapeType === 'rectangle' ? 'rectangle' : 'polygon';
      setShapeTypeInput(normalizedShape);
      setInitialShapeType(normalizedShape);
      const formattedCoordinates = formatCoordinatesForInput(building.coordinates ?? '');
      setCoordinatesInput(formattedCoordinates);
      setInitialCoordinates(trimCoordinateInput(formattedCoordinates));
      setCoordinatesError(null);
    }
  }, [isOpen, building]);

  useEffect(() => {
    setCoordinatesError(null);
  }, [coordinatesInput, shapeTypeInput]);

  const previewGeometry = useMemo(() => {
    if (coordinatesInput.trim() === '') {
      return null;
    }
    return parseBuildingGeometry(shapeTypeInput, coordinatesInput);
  }, [coordinatesInput, shapeTypeInput]);

  const hasCoordinatesInput = coordinatesInput.trim().length > 0;

  const previewBuildings = useMemo(() => {
    if (!previewGeometry) {
      return [];
    }
    return [
      {
        id: building.id,
        dongNumber: building.dongNumber,
        dongName: building.dongName ?? null,
        shapeType: shapeTypeInput,
        coordinates: previewGeometry.points
      }
    ];
  }, [previewGeometry, building, shapeTypeInput]);

  const mutation = useMutation({
    mutationFn: (payload: UpdatePayload) => updateBuildingInfo(building.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['buildings', building.complex.id] });
      queryClient.invalidateQueries({ queryKey: ['complexes'] });
      queryClient.invalidateQueries({ queryKey: ['units', building.id] });
      onClose();
      alert('동 정보가 성공적으로 수정되었습니다.');
    },
    onError: (error: Error) => {
      setErrorMessage(error.message);
    }
  });

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);
    setCoordinatesError(null);

    const trimmedPrimary = dongNumberInput.trim();
    const trimmedAlias = dongNameInput.trim();
    const normalizedCoordinates = trimCoordinateInput(coordinatesInput);

    if (trimmedPrimary === '') {
      setErrorMessage('동 이름은 비워둘 수 없습니다.');
      return;
    }

    const payload: UpdatePayload = {};
    const aliasExisting = initialDongName.length > 0 ? initialDongName : null;
    const aliasNormalized = trimmedAlias.length > 0 ? trimmedAlias : null;
    const aliasFieldChanged = aliasNormalized !== aliasExisting;
    const numericOnly = /^\d+$/.test(trimmedPrimary);

    if (numericOnly) {
      const parsedNumber = Number.parseInt(trimmedPrimary, 10);
      if (Number.isNaN(parsedNumber) || parsedNumber <= 0) {
        setErrorMessage('동 번호는 1 이상의 숫자여야 합니다.');
        return;
      }
      if (parsedNumber !== building.dongNumber) {
        payload.dongNumber = parsedNumber;
      }
    }

    let targetDongName: string | null = aliasExisting;

    if (!numericOnly) {
      targetDongName = trimmedPrimary;
    }

    if (aliasFieldChanged) {
      targetDongName = aliasNormalized;
      if (!numericOnly && aliasNormalized === null) {
        targetDongName = trimmedPrimary;
      }
    }

    if (targetDongName !== aliasExisting) {
      payload.dongName = targetDongName;
    }

    const coordinatesChanged = normalizedCoordinates !== initialCoordinates;
    const shouldValidateCoordinates =
      normalizedCoordinates !== '' &&
      (coordinatesChanged || shapeTypeInput !== initialShapeType);

    if (shapeTypeInput !== initialShapeType) {
      payload.shapeType = shapeTypeInput;
    }

    if (coordinatesChanged) {
      payload.coordinates = normalizedCoordinates === '' ? null : normalizedCoordinates;
    }

    if (shouldValidateCoordinates) {
      const parsedGeometry = parseBuildingGeometry(shapeTypeInput, normalizedCoordinates);
      if (!parsedGeometry) {
        const message = '좌표 값이 올바른 JSON 형식이 아닙니다.';
        setCoordinatesError(message);
        setErrorMessage(message);
        return;
      }
    }

    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }

    mutation.mutate(payload);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg max-w-lg w-full mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">동 정보 수정</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              동 이름 또는 번호
            </label>
            <input
              type="text"
              value={dongNumberInput}
              onChange={event => setDongNumberInput(event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="예: 101 또는 관리동"
            />
            <p className="text-xs text-gray-500 mt-1">
              숫자만 입력하면 동 번호가 변경되며, 문자나 한글을 입력하면 이 값이 주요 표시 이름으로 사용됩니다.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              동 별칭 (선택)
            </label>
            <input
              type="text"
              value={dongNameInput}
              onChange={event => setDongNameInput(event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="예: 가동, 101동 A라인"
            />
            <p className="text-xs text-gray-500 mt-1">
              비워두면 {building.dongNumber}동 으로 표시됩니다. 위 입력에서 텍스트를 지정하면 이 값보다 우선합니다.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              도형 유형
            </label>
            <select
              value={shapeTypeInput}
              onChange={event => setShapeTypeInput(event.target.value as 'polygon' | 'rectangle')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="polygon">다각형</option>
              <option value="rectangle">사각형</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              실제 좌표 데이터와 일치하는 유형을 선택하세요. 사각형은 좌표 배열이나 대각선 두 점(sw/ne) 형식 모두 지원합니다.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              좌표 데이터 (JSON)
            </label>
            <textarea
              rows={8}
              value={coordinatesInput}
              onChange={event => setCoordinatesInput(event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              placeholder='예: [{"x":127.1,"y":37.4}, ...]'
            />
            <p className="text-xs text-gray-500 mt-1">
              좌표는 Kakao 지도 좌표계(WGS84) 기준 x(lng), y(lat) 값으로 구성된 배열이어야 합니다.
            </p>
            {coordinatesError && (
              <div className="mt-2 text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded">
                {coordinatesError}
              </div>
            )}
          </div>

  <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              좌표 미리보기
            </label>
            {previewGeometry ? (
              <KakaoMap
                height="220px"
                buildings={previewBuildings}
                focusBuildingId={building.id}
              />
            ) : (
              <div className="text-sm text-gray-500 border border-dashed border-gray-300 rounded-md px-3 py-6 text-center">
                {hasCoordinatesInput
                  ? '좌표 데이터를 파싱할 수 없습니다. JSON 형식을 확인해주세요.'
                  : '좌표 데이터를 입력하면 미리보기가 표시됩니다.'}
              </div>
            )}
          </div>

          {errorMessage && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded">
              {errorMessage}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              disabled={mutation.isPending}
            >
              취소
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? '저장 중...' : '저장'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BuildingInfoEditModal;
